import React, { useState, useEffect } from 'react';
import './Schedule.css';

function Schedule({ apiEndpoint, idToken, isManager }) {
  const [schedule, setSchedule] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [outOfServiceNote, setOutOfServiceNote] = useState('');

  useEffect(() => {
    fetchSchedule();
  }, []);

  const fetchSchedule = async () => {
    try {
      const response = await fetch(`${apiEndpoint}/schedule`, {
        headers: { Authorization: idToken }
      });
      const data = await response.json();
      setSchedule(data.schedule || []);
    } catch (error) {
      console.error('Error fetching schedule:', error);
    }
  };

  const addSchedule = async (date, type = 'booking', note = '') => {
    try {
      await fetch(`${apiEndpoint}/schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: idToken },
        body: JSON.stringify({ date, type, note })
      });
      fetchSchedule();
      setShowModal(false);
      setOutOfServiceNote('');
    } catch (error) {
      console.error('Error adding schedule:', error);
    }
  };

  const deleteSchedule = async (date) => {
    try {
      await fetch(`${apiEndpoint}/schedule`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: idToken },
        body: JSON.stringify({ date })
      });
      fetchSchedule();
      setShowModal(false);
    } catch (error) {
      console.error('Error deleting schedule:', error);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    for (let i = 0; i < firstDay.getDay(); i++) days.push(null);
    for (let i = 1; i <= lastDay.getDate(); i++) days.push(new Date(year, month, i));
    return days;
  };

  const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getScheduleForDate = (date) => {
    const dateStr = formatDate(date);
    return schedule.find(s => s.date === dateStr);
  };

  const handleDayClick = (date) => {
    setSelectedDate(date);
    setShowModal(true);
  };

  const changeMonth = (offset) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + offset, 1));
  };

  const days = getDaysInMonth(currentMonth);
  const monthName = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const scheduleEntry = selectedDate ? getScheduleForDate(selectedDate) : null;
  const isOutOfService = scheduleEntry?.type === 'out-of-service';

  const getDayClass = (entry) => {
    if (!entry) return 'available';
    return entry.type === 'out-of-service' ? 'out-of-service' : 'booked';
  };

  const canRemove = scheduleEntry && (isManager || !isOutOfService);

  return (
    <div className="schedule-container">
      <h2>Aircraft Schedule</h2>
      <div className="schedule-legend">
        <span className="legend-item"><span className="legend-dot available"></span>Available</span>
        <span className="legend-item"><span className="legend-dot booked"></span>Booked</span>
        <span className="legend-item"><span className="legend-dot out-of-service"></span>Out of Service</span>
      </div>
      <div className="calendar-header">
        <button onClick={() => changeMonth(-1)}>←</button>
        <h3>{monthName}</h3>
        <button onClick={() => changeMonth(1)}>→</button>
      </div>
      <div className="calendar">
        <div className="calendar-weekdays">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="weekday">{day}</div>
          ))}
        </div>
        <div className="calendar-days">
          {days.map((date, index) => {
            if (!date) return <div key={index} className="calendar-day empty"></div>;
            const entry = getScheduleForDate(date);
            const isToday = formatDate(date) === formatDate(new Date());
            return (
              <div
                key={index}
                className={`calendar-day ${getDayClass(entry)} ${isToday ? 'today' : ''}`}
                onClick={() => handleDayClick(date)}
              >
                <div className="day-number">{date.getDate()}</div>
                {entry && <div className="day-label">{entry.type === 'out-of-service' ? '🔧' : entry.userName}</div>}
              </div>
            );
          })}
        </div>
      </div>

      {showModal && selectedDate && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{formatDate(selectedDate)}</h3>
            {scheduleEntry ? (
              <>
                {isOutOfService ? (
                  <p className="oos-status">🔧 Out of Service{scheduleEntry.note ? `: ${scheduleEntry.note}` : ''}</p>
                ) : (
                  <p className="booked-status">Booked by: {scheduleEntry.userName}</p>
                )}
                {canRemove && (
                  <button onClick={() => deleteSchedule(formatDate(selectedDate))} className="remove-btn">
                    Remove {isOutOfService ? 'Out of Service' : 'Booking'}
                  </button>
                )}
              </>
            ) : (
              <>
                <p className="available-status">Available</p>
                <button onClick={() => addSchedule(formatDate(selectedDate))} className="book-btn">
                  Book This Date
                </button>
                {isManager && (
                  <div className="oos-section">
                    <input
                      type="text"
                      placeholder="Reason (optional)"
                      value={outOfServiceNote}
                      onChange={(e) => setOutOfServiceNote(e.target.value)}
                      className="oos-input"
                    />
                    <button onClick={() => addSchedule(formatDate(selectedDate), 'out-of-service', outOfServiceNote)} className="oos-btn">
                      🔧 Mark Out of Service
                    </button>
                  </div>
                )}
              </>
            )}
            <button onClick={() => setShowModal(false)} className="close-btn">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Schedule;